import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { CartItemWithPrice } from "@/types/api";
import { formatCurrency } from "@/utils/utils";

export type CartContextType = {
  cart: CartItemWithPrice[];
  total: string;
  addToCart: (item: CartItemWithPrice) => void;
  removeFromCart: (item: CartItemWithPrice) => void;
  clear: () => void;
  setCurrency: (currency: string) => void;
};

// NOTE: leaving this here as a potential cart-retain addition
// const addToCart = (item: CartItemWithPrice) => {
//   const currentItems = localStorage.getItem(LOCAL_STORAGE_CART_KEY);
//   const newItems = currentItems != null ? JSON.parse(currentItems) : [item.id];
//   localStorage.setItem(LOCAL_STORAGE_CART_KEY, newItems);
//   setCart(prev => [...prev, item]);
// };

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItemWithPrice[]>([]);
  const [total, setTotal] = useState<string>("$0.00");
  const [currency, setCurrency] = useState<string>("USD");

  useEffect(() => {
    const sum = cart.reduce((acc, item) => {
      const price = item.salePriceActive && item.salePrice != null ? item.salePrice : item.price ?? 0;
      return acc + price;
    }, 0);

    setTotal(sum == 0 ? "" : formatCurrency(sum, currency));
  }, [cart]);

  const addToCart = (item: CartItemWithPrice) => setCart(prev => [...prev, item]);
  const removeFromCart = (item: CartItemWithPrice) => setCart(prev => prev.filter(i => i.id !== item.id));
  const clear = () => setCart([]);

  return (
    <CartContext.Provider value={{ cart, total, addToCart, removeFromCart, clear, setCurrency }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within a CartProvider");
  return context;
}

